import React, { useEffect, useRef, useState } from 'react';
import { suggestAddress } from './propertyTaxApi';
import { Input } from '../../components/ui/input';

const DEBOUNCE_MS = 600;
const CACHE_MAX = 50;

// Each search is a paid vendor request: wait for a house number and 3 letters of the street.
const searchable = (q) => {
  const m = q.match(/^\d+\S*\s+(.*)$/);
  return Boolean(m) && (m[1].match(/[a-z]/gi) || []).length >= 3;
};

// Ticket 131: free-text address input with Geoapify suggestions. The input is
// always free text (manual override): suggestions only replace it on a pick.
// Only typing fetches; a pick or a programmatic `value` change (loaded
// worksheet) does not. `state` narrows the search to one state; empty = whole US.
// Answers are remembered while the field is mounted, so retyping does not search again.
export default function AddressAutocomplete({ value, onChange, onPick, state, id, testid, className, placeholder }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false); // the vendor can take a few seconds
  const reqRef = useRef(0);
  const typedRef = useRef(false); // true only when the latest value change came from typing
  const offRef = useRef(false); // server said suggestions are disabled
  const stateRef = useRef(state); // read at fetch time; a state change alone does not fetch
  const cacheRef = useRef(new Map()); // `${state}|${text}` -> suggestions; never holds an empty answer
  stateRef.current = state;
  const text = value ?? '';

  useEffect(() => {
    const q = text.trim();
    if (!typedRef.current) return undefined;
    typedRef.current = false;
    const req = ++reqRef.current;
    if (!searchable(q) || offRef.current) {
      setItems([]);
      setOpen(false);
      setBusy(false);
      return undefined;
    }
    const key = `${stateRef.current || ''}|${q.toLowerCase()}`;
    const known = cacheRef.current.get(key);
    if (known) {
      setBusy(false);
      setItems(known);
      setActive(-1);
      setOpen(true);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await suggestAddress(q, stateRef.current);
        const list = (r && r.suggestions) || [];
        if (list.length > 0) {
          const cache = cacheRef.current;
          if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
          cache.set(key, list);
        }
        if (reqRef.current !== req) return;
        setBusy(false);
        if (r && r.enabled === false) offRef.current = true;
        setItems(list);
        setActive(-1);
        setOpen(list.length > 0);
      } catch (e) {
        if (reqRef.current === req) { setItems([]); setOpen(false); setBusy(false); }
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const listId = `${testid}-suggestions`;
  const close = () => { setOpen(false); setActive(-1); setBusy(false); };
  const pick = (s) => {
    typedRef.current = false;
    reqRef.current += 1; // drop any response still in flight
    onChange(s.label);
    if (onPick) onPick(s);
    setItems([]);
    close();
  };
  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % items.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      pick(items[active]);
    } else if (e.key === 'Escape') {
      close();
    }
  };

  return (
    <div className="relative">
      <Input
        type="text"
        id={id}
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => { typedRef.current = true; onChange(e.target.value); }}
        onKeyDown={onKeyDown}
        onBlur={() => { reqRef.current += 1; close(); }} // a late response must not reopen the list
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? `${testid}-suggestion-${active}` : undefined}
        data-testid={testid}
        className={className}
      />
      {busy && !open && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none" data-testid={`${testid}-searching`}>
          Searching…
        </div>
      )}
      {open && items.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          data-testid={listId}
          className="absolute z-50 left-0 right-0 mt-1 rounded-md border border-slate-200 bg-white shadow-lg text-sm overflow-hidden"
        >
          {items.map((s, i) => (
            <li
              key={s.label}
              id={`${testid}-suggestion-${i}`}
              role="option"
              aria-selected={i === active}
              data-testid={`${testid}-suggestion-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-1.5 cursor-pointer ${i === active ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
            >
              {s.label}
            </li>
          ))}
          <li role="presentation" className="px-3 py-1 text-[11px] text-slate-400 border-t border-slate-100">
            <a
              href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer"
              data-testid={`${testid}-attribution`}
              onMouseDown={(e) => e.preventDefault()}
              className="hover:underline"
            >
              Powered by Geoapify
            </a>
          </li>
        </ul>
      )}
    </div>
  );
}
